import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Lock, Loader2, LogOut, FileDown, ChevronLeft, Inbox, ShieldCheck, KeyRound, Plus, Trash2, Copy, Check, FileArchive } from "lucide-react";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "iclean_admin_token";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const Field = ({ label, value }) => (
  <div className="py-2.5 border-b border-neutral-100 grid grid-cols-3 gap-3">
    <div className="text-sm text-neutral-500">{label}</div>
    <div className="col-span-2 text-sm font-medium text-[#1a1714] break-words">{value || "-"}</div>
  </div>
);

const LoginView = ({ onLogin }) => {
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeId, setChallengeId] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) { localStorage.setItem(TOKEN_KEY, data.token); onLogin(data.token); return; }
      setChallengeId(data.challengeId);
      setChannel(data.channel);
      setSentTo(data.sentTo);
      setStep("otp");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the code we sent you."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { challengeId, code: code.trim() });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not verify code.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/resend-otp`, { challengeId });
      setSentTo(data.sentTo);
      setCode("");
      toast.success(`New code emailed to ${data.sentTo}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not resend code.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#fdf1e7]">
      {step === "password" ? (
        <form onSubmit={submitPassword} className="max-w-sm w-full bg-white rounded-[28px] p-9 border border-neutral-100 soft-shadow">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center">
            <Lock size={28} />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-[#1a1714] text-center">Admin sign in</h1>
          <p className="mt-2 text-sm text-neutral-500 text-center">iClean NW DBS submissions</p>
          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="Enter your email" />
            </div>
            <div>
              <Label className="text-sm font-medium">Password</Label>
              <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="Your password" />
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-6 py-3 rounded-full transition-all">
            {loading ? <><Loader2 size={17} className="animate-spin" /> Checking...</> : "Continue"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitOtp} className="max-w-sm w-full bg-white rounded-[28px] p-9 border border-neutral-100 soft-shadow">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center">
            <KeyRound size={28} />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-[#1a1714] text-center">Enter your code</h1>
          <p className="mt-2 text-sm text-neutral-500 text-center">
            We emailed a 6-digit code to <span className="font-semibold text-[#1a1714]">{sentTo}</span>.
          </p>
          <Input
            data-testid="admin-otp"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="mt-6 h-14 rounded-xl text-center text-2xl tracking-[0.5em] font-bold"
            placeholder="------"
          />
          <button type="submit" disabled={loading} data-testid="admin-otp-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-6 py-3 rounded-full transition-all">
            {loading ? <><Loader2 size={17} className="animate-spin" /> Verifying...</> : "Verify and sign in"}
          </button>
          <div className="mt-5 flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={() => resend()} data-testid="admin-resend-btn" className="text-[#ea731d] font-semibold hover:underline">Resend code</button>
            <button type="button" onClick={() => { setStep("password"); setCode(""); }} className="text-neutral-400 hover:text-[#1a1714] transition-colors mt-1">Back</button>
          </div>
        </form>
      )}
    </div>
  );
};

const CodesView = ({ token, onLogout }) => {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [codes, setCodes] = useState(null);
  const [label, setLabel] = useState("");
  const [custom, setCustom] = useState("");
  const [oneTime, setOneTime] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/codes`, authHeader);
      setCodes(data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Could not load access codes.");
      setCodes([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/codes`, { code: custom.trim(), label: label.trim(), oneTime }, authHeader);
      setCodes((c) => [data, ...(c || [])]);
      setLabel(""); setCustom(""); setOneTime(false);
      toast.success(`Code ${data.code} created`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create code.");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete access code "${c.code}"? Candidates using it will no longer be able to submit.`)) return;
    try {
      await axios.delete(`${API}/admin/codes/${c.id}`, authHeader);
      setCodes((list) => list.filter((x) => x.id !== c.id));
      toast.success("Code deleted");
    } catch {
      toast.error("Could not delete code.");
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <>
      <h1 className="text-2xl font-bold text-[#1a1714]">Access codes</h1>
      <p className="mt-1.5 text-sm text-neutral-500 max-w-xl">Share a code with candidates so they can open the DBS check page. Create as many as you like and delete any you no longer want to work.</p>

      <form onSubmit={create} className="mt-6 bg-white rounded-2xl border border-neutral-200/70 p-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Label <span className="text-neutral-400 font-normal">(optional)</span></Label>
            <Input data-testid="code-label" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-2 h-11 rounded-xl" placeholder="e.g. September intake" />
          </div>
          <div>
            <Label className="text-sm font-medium">Custom code <span className="text-neutral-400 font-normal">(blank = auto-generate)</span></Label>
            <Input data-testid="code-custom" value={custom} onChange={(e) => setCustom(e.target.value)} className="mt-2 h-11 rounded-xl uppercase" placeholder="e.g. ICLEAN-JANE" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox data-testid="code-onetime" checked={oneTime} onCheckedChange={(v) => setOneTime(!!v)} />
            <span className="text-sm text-neutral-700">Single use <span className="text-neutral-400">(expires automatically after one candidate submits)</span></span>
          </label>
          <button type="submit" disabled={creating} data-testid="code-create-btn" className="h-11 inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-6 rounded-full transition-colors">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create code
          </button>
        </div>
      </form>

      {codes === null ? (
        <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-[#ea731d]" /></div>
      ) : codes.length === 0 ? (
        <div className="mt-8 text-center text-neutral-500">
          <KeyRound size={38} className="mx-auto text-neutral-300" />
          <p className="mt-3">No access codes yet. Create one above.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="codes-list">
          {codes.map((c) => (
            <div key={c.id} data-testid={`code-row-${c.code}`} className="bg-white rounded-2xl border border-neutral-200/70 p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className={`font-mono font-bold tracking-wider text-lg ${c.used ? "text-neutral-400 line-through" : "text-[#1a1714]"}`}>{c.code}</span>
                  {!c.used && (
                    <button onClick={() => copy(c.code)} className="text-neutral-400 hover:text-[#ea731d] transition-colors" aria-label="Copy code">
                      {copied === c.code ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                  )}
                  {c.oneTime && !c.used && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#ea731d]/12 text-[#ea731d]">One-time</span>}
                  {c.used && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500">Used</span>}
                </div>
                {c.label && <div className="text-sm text-neutral-500 mt-0.5 truncate">{c.label}</div>}
              </div>
              <button onClick={() => remove(c)} data-testid={`code-delete-${c.code}`} className="flex-shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-neutral-500 hover:text-red-600 transition-colors">
                <Trash2 size={16} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const SettingsView = ({ token, onLogout }) => {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/change-password`, { currentPassword: current, newPassword: next }, authHeader);
      toast.success("Password changed successfully.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error(typeof d === "string" ? d : "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm("Run data cleanup now? Submissions older than 90 days will have their documents and extra personal details removed (name, address, NI and date of birth are kept for payroll).")) return;
    setPurging(true);
    try {
      const { data } = await axios.post(`${API}/admin/purge-now`, {}, authHeader);
      toast.success(data.purged ? `Cleaned ${data.purged} old submission(s).` : "Nothing to clean - no submissions older than 90 days.");
    } catch {
      toast.error("Could not run cleanup.");
    } finally {
      setPurging(false);
    }
  };

  return (
    <>
      <h1 className="text-2xl font-bold text-[#1a1714]">Settings</h1>

      <form onSubmit={changePassword} className="mt-6 bg-white rounded-2xl border border-neutral-200/70 p-6 max-w-md">
        <h2 className="font-semibold text-[#1a1714]">Change password</h2>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-sm font-medium">Current password</Label>
            <Input data-testid="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-2 h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-sm font-medium">New password <span className="text-neutral-400 font-normal">(min 8 characters)</span></Label>
            <Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-2 h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-sm font-medium">Confirm new password</Label>
            <Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 h-11 rounded-xl" />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="cp-submit" className="mt-5 inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-6 py-2.5 rounded-full transition-colors">
          {saving ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : "Update password"}
        </button>
      </form>

      <div className="mt-6 bg-white rounded-2xl border border-neutral-200/70 p-6 max-w-md">
        <h2 className="font-semibold text-[#1a1714]">Data retention</h2>
        <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
          To stay GDPR-friendly, submissions are automatically cleaned <span className="font-semibold">90 days</span> after they arrive: uploaded documents and extra personal details are removed, while the <span className="font-semibold">name, address, National Insurance number and date of birth are kept for payroll</span>. This runs on its own every day - you can also run it now.
        </p>
        <button onClick={runCleanup} disabled={purging} data-testid="run-cleanup-btn" className="mt-4 inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-[#ea731d] hover:text-[#ea731d] disabled:opacity-70 text-neutral-700 font-semibold px-5 py-2.5 rounded-full transition-colors">
          {purging ? <><Loader2 size={16} className="animate-spin" /> Cleaning...</> : <><Trash2 size={16} /> Run cleanup now</>}
        </button>
      </div>
    </>
  );
};

const ApplicationsView = ({ token, onLogout }) => {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [apps, setApps] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cvLoading, setCvLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/applications`, authHeader);
      setApps(data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Could not load applications.");
      setApps([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const downloadCv = async () => {
    setCvLoading(true);
    try {
      const res = await axios.get(`${API}/admin/applications/${selected.id}/cv`, { ...authHeader, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = selected.cvFileName || "cv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download CV.");
    } finally {
      setCvLoading(false);
    }
  };

  const name = (a) => `${a.firstName || ""} ${a.lastName || ""}`.trim() || "Applicant";

  if (selected) {
    const rows = [
      ["Email", selected.email], ["Phone", selected.phone], ["Location", selected.location],
      ["Position", selected.position], ["Experience (years)", selected.experienceYears],
      ["Driving licence", selected.drivingLicence], ["Own transport", selected.ownTransport],
      ["Right to work in UK", selected.rightToWork], ["Employment type", selected.employmentType],
      ["Availability", (selected.availability || []).join(", ")], ["Available from", selected.startDate],
    ];
    return (
      <>
        <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#ea731d] transition-colors">
          <ChevronLeft size={17} /> Back to all
        </button>
        <div className="mt-4 bg-white rounded-[24px] border border-neutral-200/70 p-6 lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-[#1a1714]">{name(selected)}</h1>
              <span className="text-xs text-neutral-400">{fmtDate(selected.timestamp)}</span>
            </div>
            {selected.cvFileName && (
              <button onClick={downloadCv} disabled={cvLoading} data-testid="app-cv-download" className="inline-flex items-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                {cvLoading ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download CV
              </button>
            )}
          </div>
          <div className="mt-5">
            {rows.map(([k, v]) => (
              <Field key={k} label={k} value={v} />
            ))}
          </div>
          <h3 className="mt-6 mb-1 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Previous experience</h3>
          <p className="text-sm text-neutral-700 whitespace-pre-line">{selected.experience || "-"}</p>
          {selected.notes && (
            <>
              <h3 className="mt-6 mb-1 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Additional notes</h3>
              <p className="text-sm text-neutral-700 whitespace-pre-line">{selected.notes}</p>
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-bold text-[#1a1714]">Job applications</h1>
      {apps === null ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#ea731d]" /></div>
      ) : apps.length === 0 ? (
        <div className="mt-10 text-center text-neutral-500">
          <Inbox size={40} className="mx-auto text-neutral-300" />
          <p className="mt-3">No applications yet.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="applications-list">
          {apps.map((a) => (
            <button key={a.id} data-testid={`app-${a.id}`} onClick={() => setSelected(a)} className="w-full text-left bg-white rounded-2xl border border-neutral-200/70 p-5 hover:border-[#ea731d]/50 hover:-translate-y-0.5 transition-all">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-semibold text-[#1a1714] truncate">{name(a)}</div>
                  <div className="text-sm text-neutral-500 truncate">{a.email} &middot; {a.phone}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs text-neutral-400">{fmtDate(a.timestamp)}</div>
                  {a.cvFileName && <div className="text-xs font-semibold text-[#ea731d] mt-1">CV attached</div>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [subs, setSubs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [tab, setTab] = useState("submissions");

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/dbs`, authHeader);
      setSubs(data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Could not load submissions.");
      setSubs([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await axios.get(`${API}/admin/dbs/${selected.id}/document/${doc.id}`, { ...authHeader, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download document.");
    } finally {
      setDownloading(null);
    }
  };

  const exportSubmission = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(`${API}/admin/dbs/${selected.id}/export.${kind}`, { ...authHeader, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(selected.firstName || "candidate")}-${(selected.lastName || "")}-dbs.${kind}`.replace(/\s+/g, "");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Could not export ${kind.toUpperCase()}.`);
    } finally {
      setExporting(null);
    }
  };

  const fullName = (s) => [s.title, s.firstName, s.middleNames, s.lastName].filter(Boolean).join(" ");

  return (
    <div className="min-h-screen bg-[#fdf1e7]">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 font-bold text-[#1a1714]">
            <ShieldCheck size={20} className="text-[#ea731d]" /> iClean NW Admin
          </div>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-[#ea731d] transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-6 flex gap-1">
          <button
            onClick={() => { setTab("submissions"); setSelected(null); }}
            data-testid="tab-submissions"
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === "submissions" ? "border-[#ea731d] text-[#ea731d]" : "border-transparent text-neutral-500 hover:text-[#1a1714]"}`}
          >
            DBS checks
          </button>
          <button
            onClick={() => { setTab("applications"); setSelected(null); }}
            data-testid="tab-applications"
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === "applications" ? "border-[#ea731d] text-[#ea731d]" : "border-transparent text-neutral-500 hover:text-[#1a1714]"}`}
          >
            Applications
          </button>
          <button
            onClick={() => { setTab("codes"); setSelected(null); }}
            data-testid="tab-codes"
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === "codes" ? "border-[#ea731d] text-[#ea731d]" : "border-transparent text-neutral-500 hover:text-[#1a1714]"}`}
          >
            Access codes
          </button>
          <button
            onClick={() => { setTab("settings"); setSelected(null); }}
            data-testid="tab-settings"
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === "settings" ? "border-[#ea731d] text-[#ea731d]" : "border-transparent text-neutral-500 hover:text-[#1a1714]"}`}
          >
            Settings
          </button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 lg:px-6 py-8">
        {tab === "codes" ? (
          <CodesView token={token} onLogout={onLogout} />
        ) : tab === "applications" ? (
          <ApplicationsView token={token} onLogout={onLogout} />
        ) : tab === "settings" ? (
          <SettingsView token={token} onLogout={onLogout} />
        ) : !selected ? (
          <>
            <h1 className="text-2xl font-bold text-[#1a1714]">DBS submissions</h1>
            {subs === null ? (
              <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#ea731d]" /></div>
            ) : subs.length === 0 ? (
              <div className="mt-10 text-center text-neutral-500">
                <Inbox size={40} className="mx-auto text-neutral-300" />
                <p className="mt-3">No submissions yet.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-3" data-testid="admin-submissions-list">
                {subs.map((s) => (
                  <button key={s.id} data-testid={`admin-sub-${s.id}`} onClick={() => setSelected(s)} className="w-full text-left bg-white rounded-2xl border border-neutral-200/70 p-5 hover:border-[#ea731d]/50 hover:-translate-y-0.5 transition-all">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#1a1714] truncate">{fullName(s)}</div>
                        <div className="text-sm text-neutral-500 truncate">{s.email} &middot; {s.phone}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-neutral-400">{fmtDate(s.timestamp)}</div>
                        <div className="text-xs font-semibold text-[#ea731d] mt-1">{(s.documents || []).length} document(s)</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#ea731d] transition-colors">
              <ChevronLeft size={17} /> Back to all
            </button>
            <div className="mt-4 bg-white rounded-[24px] border border-neutral-200/70 p-6 lg:p-8">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="text-xl font-bold text-[#1a1714]">{fullName(selected)}</h1>
                  <span className="text-xs text-neutral-400">{fmtDate(selected.timestamp)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => exportSubmission("pdf")} disabled={exporting === "pdf"} data-testid="export-pdf-btn" className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[#ea731d] hover:text-[#ea731d] disabled:opacity-70 text-neutral-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                    {exporting === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} PDF
                  </button>
                  <button onClick={() => exportSubmission("zip")} disabled={exporting === "zip"} data-testid="export-zip-btn" className="inline-flex items-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                    {exporting === "zip" ? <Loader2 size={15} className="animate-spin" /> : <FileArchive size={15} />} ZIP + files
                  </button>
                </div>
              </div>
              {selected.purged && (
                <div className="mt-3 text-sm rounded-xl bg-neutral-100 text-neutral-600 px-4 py-3">
                  This submission is over 90 days old. Documents and extra personal details were automatically removed for GDPR. Name, address, National Insurance number and date of birth are kept for payroll.
                </div>
              )}

              <h3 className="mt-6 mb-1 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Personal</h3>
              <Field label="Maiden / previous surname" value={selected.maidenName} />
              <Field label="Other names used" value={selected.otherNames} />
              <Field label="Mother's maiden name" value={selected.mothersMaidenName} />
              <Field label="Date of birth" value={selected.dateOfBirth} />
              <Field label="Gender" value={selected.gender} />
              <Field label="Town of birth" value={selected.townOfBirth} />
              <Field label="Country of birth" value={selected.countryOfBirth} />
              <Field label="Nationality" value={selected.nationality} />
              <Field label="National Insurance no." value={selected.nationalInsurance} />

              <h3 className="mt-6 mb-1 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Contact</h3>
              <Field label="Email" value={selected.email} />
              <Field label="Phone" value={selected.phone} />

              <h3 className="mt-6 mb-1 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Address history</h3>
              <Field label="Current address" value={`${selected.currentAddress?.line || ""}, ${selected.currentAddress?.postcode || ""}, ${selected.currentAddress?.country || ""} (from ${selected.currentAddress?.fromDate || "-"})`} />
              {(selected.previousAddresses || []).map((a, i) => (
                <Field key={i} label={`Previous ${i + 1}`} value={`${a.line}, ${a.postcode}, ${a.country} (${a.fromDate} to ${a.toDate})`} />
              ))}

              <h3 className="mt-6 mb-3 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Documents</h3>
              {(selected.documents || []).length === 0 ? (
                <p className="text-sm text-neutral-500">No documents uploaded.</p>
              ) : (
                <div className="space-y-2.5">
                  {selected.documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-[#f7f7f5] px-4 py-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#1a1714]">{d.label}</div>
                        <div className="text-xs text-neutral-500 truncate">{d.original_filename}</div>
                      </div>
                      <button onClick={() => download(d)} disabled={downloading === d.id} data-testid={`admin-download-${d.id}`} className="flex-shrink-0 inline-flex items-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                        {downloading === d.id ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <>
      <Seo title="Admin | iClean NW" description="iClean NW admin area." path="/admin" noindex />
      {token ? <Dashboard token={token} onLogout={logout} /> : <LoginView onLogin={setToken} />}
    </>
  );
};

export default Admin;
